/**
 * `/press` — the unified cross-batch PRESS STATION (roadmap Phase 2 / B3).
 *
 * Shop-wide "what still needs pressing" across BOTH techniques: every READY gang-sheet batch that
 * still has an unpressed placement — screen print OR DTF — oldest batch first. Each placement is
 * checked off individually; a garment completes (its line flips PRINTED, one blank is consumed) once
 * every placement on it — screen print AND DTF — is pressed. This is the same per-garment settle that
 * the order job card drives; the station just lets the floor work by press setup across many orders.
 *
 * Check-off dispatches by technique to the EXISTING mark endpoints (screen print →
 * /screen-print/worklist/mark, DTF → /batches/:id/press) — no new money path. Reads/writes go through
 * the authed API client. Marking is single-flight (all checkboxes disable while a toggle is in flight)
 * so two rapid clicks can't race the server's optimistic lock. A screen-print RUN can also be finished
 * in one action ("Mark all pressed") — the same per-placement endpoint driven SEQUENTIALLY over the
 * run's garments (a screen is set up once and every garment runs under it); DTF stays per-garment.
 */

import type { BlankOnHand, PressMethod, PressWorkItem, PressWorklistBatch } from '@fsp/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Brush,
  Check,
  CheckCircle2,
  ChevronsUpDown,
  ListFilter,
  Loader2,
  Printer,
  RefreshCw,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useApiClient } from '@/lib/api/hooks';
import { clearQueryStale, useQueryStaleSignal } from '@/lib/stale-signal';
import { cn } from '@/lib/utils';
import { pullChipLabel, pullState } from './batch-lib';
import { invalidateInventory } from './inventory-lib';
import { relativeAge } from './orders-queue';
import {
  addPending,
  dropPending,
  expirePending,
  type PendingMarks,
  prunePending,
  reapplyPending,
} from './press-pending-overlay';
import { PressPullSheet } from './press-pull-sheet';
import {
  type BlankPullRow,
  batchLabelOf,
  blankPullList,
  filterWorklistBatches,
  groupByPressRun,
  isPerCopy,
  methodLabelOf,
  type OptimisticMarkTarget,
  type PressRun,
  type PressRunItem,
  pressedCopiesOf,
} from './press-station-lib';
import { PullChip } from './pull-chip';
import { useBlankNames } from './use-blank-names';

// The mark mutation's variables ARE an optimistic target — which placement, and its new pressed state.
type MarkVars = OptimisticMarkTarget;

type MethodFilter = 'all' | PressMethod;

/** One selectable batch in the queue filter — label, who it's for, how many garments are left, and the
 *  pull state in short form (pulled-blanks v2: the presser sees which sheets are pulled before setting up). */
type BatchOption = {
  batchId: string;
  label: string;
  brand: string;
  outstanding: number;
  pull: string;
};

/**
 * Multi-select popover to pick which batches the station shows — "we only want these two, so we can
 * see how to queue them". Empty selection = every batch (the default). Picking toggles a batch in/out
 * without closing, so several can be chosen in a row; "All batches" clears back to the default.
 */
function BatchFilter({
  options,
  selected,
  onToggle,
  onClear,
}: {
  options: BatchOption[];
  selected: ReadonlySet<string>;
  onToggle: (batchId: string) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const count = options.reduce((n, o) => (selected.has(o.batchId) ? n + 1 : n), 0);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          className="justify-between gap-2"
        >
          <ListFilter className="size-4" />
          {count > 0 ? `${count} batch${count === 1 ? '' : 'es'}` : 'All batches'}
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="end">
        <Command>
          <CommandInput placeholder="Filter batches…" />
          <CommandList>
            <CommandEmpty>No batches.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__all-batches__" onSelect={onClear} aria-label="Show all batches">
                <div className="flex flex-1 items-center justify-between">
                  <span className={count === 0 ? 'font-medium' : undefined}>All batches</span>
                  {count === 0 && <Check className="size-4 text-primary" />}
                </div>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Pick batches to queue">
              {options.map((o) => {
                const isSelected = selected.has(o.batchId);
                return (
                  <CommandItem
                    key={o.batchId}
                    value={`${o.label} ${o.brand}`}
                    onSelect={() => onToggle(o.batchId)}
                  >
                    <div className="flex flex-1 items-center justify-between gap-3">
                      <span className="truncate">
                        <span className="font-medium">{o.label}</span>
                        <span className="text-xs text-muted-foreground">
                          {' '}
                          · {o.brand}
                          {o.pull ? ` · ${o.pull}` : ''}
                        </span>
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        <span
                          className="text-xs tabular-nums text-muted-foreground"
                          title="Garments still to press"
                        >
                          {o.outstanding}
                        </span>
                        {isSelected && <Check className="size-4 text-primary" />}
                      </span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Small technique tag so a mixed worklist stays scannable. */
function MethodBadge({ method }: { method: PressMethod }) {
  return (
    <Badge variant="secondary" className="font-normal">
      {methodLabelOf(method)}
    </Badge>
  );
}

/**
 * Compact "blanks to pull" summary above a press run's garment list — one line per physical blank
 * (style·colour·size) and how many to grab. Turns the per-order rows into the operator's shelf-pull
 * list. Hover a row to see which products collapsed onto that blank.
 */
function BlankPullStrip({
  rows,
  sheets = [],
}: {
  rows: BlankPullRow[];
  /** The sheets this run spans and each one's pull state (pulled-blanks v2 — state only; Phase 2b adds
   *  the per-run tap). */
  sheets?: readonly { label: string; pull: string }[];
}) {
  if (rows.length === 0) return null;
  const total = rows.reduce((s, r) => s + r.quantity, 0);
  return (
    <div className="mb-3 rounded-md border bg-muted/30 p-3">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Blanks to pull
        </span>
        <span className="text-xs tabular-nums text-muted-foreground">
          {total} garment{total === 1 ? '' : 's'}
        </span>
      </div>
      {sheets.length > 0 && (
        <p className="mb-1.5 text-xs text-muted-foreground" data-testid="strip-pull-state">
          {sheets.map((s) => `${s.label} ${s.pull}`).join(' · ')}
        </p>
      )}
      <ul className="space-y-0.5 text-sm">
        {rows.map((r) => (
          <li
            key={r.key}
            className="flex items-baseline justify-between gap-3"
            title={r.titles.join(', ')}
          >
            <span>
              <span className="font-medium">{r.label}</span>
              {r.variant && <span className="text-muted-foreground"> · {r.variant}</span>}
            </span>
            <span className="font-semibold tabular-nums">{r.quantity}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PressStationPage() {
  const client = useApiClient();
  const qc = useQueryClient();
  // 'batch' = one card per gang sheet; 'run' = regroup by (technique + design + location) across
  // sheets, mirroring how a press is set up — mount one screen / load one design, run every garment.
  const [view, setView] = useState<'batch' | 'run'>('batch');
  // Filter the station to one technique when the floor is working a single press.
  const [methodFilter, setMethodFilter] = useState<MethodFilter>('all');
  // Narrow the station to just the batches the operator wants to queue next (empty = all batches).
  // Batch ids are stable across refetches, so the selection survives a mark/refresh; a batch that
  // presses out simply stops matching.
  const [selectedBatchIds, setSelectedBatchIds] = useState<Set<string>>(() => new Set());
  const toggleBatch = (id: string) =>
    setSelectedBatchIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clearFilters = () => {
    setSelectedBatchIds(new Set());
    setMethodFilter('all');
  };

  /**
   * Marks awaiting server confirmation — the optimistic state, and the fix for a real money bug. See
   * `press-pending-overlay.ts`: `cancelQueries` in `onMutate` cannot cancel a refetch that starts AFTER
   * it, and every read behind `/press/worklist` is eventually consistent, so a response from a pre-write
   * replica used to un-check a box the operator had already ticked — and they pressed the garment twice.
   *
   * ⚠️ Optimism deliberately lives HERE and no longer in the query cache. The cache must stay pure server
   * truth, because `prunePending` decides an entry is confirmed by comparing it against cached data — and
   * an optimistic write into the cache would look exactly like server agreement and retire the entry
   * instantly, leaving nothing to re-apply. (`setQueryData` also runs `structuralSharing`, so doing this
   * inside that hook has the same defect.) State, not a ref, so adding a mark re-renders the checkbox.
   */
  const [pending, setPending] = useState<PendingMarks>(() => new Map());

  const worklistQuery = useQuery({
    queryKey: ['press-worklist'],
    queryFn: async () => {
      const { data, error } = await client.GET('/press/worklist');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the worklist.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  // Phase 6: the change feed marks this key stale WITHOUT refetching it (MARK_STALE_ONLY), because
  // refreshing the worklist under a finger can move a tap onto the wrong garment and fire settleLine
  // against it. So freshness is OFFERED here — a badge on Refresh — and applied only when the operator
  // taps, with their hands clear. Deliberately NOT `worklistQuery.isStale`: the global 30 s staleTime
  // makes that true almost always, and this page invalidates its own key in `onSettled`, so no
  // cache-derived flag can tell another station's change from the operator's own press.
  const worklistMoved = useQueryStaleSignal('press-worklist');
  // Any successful fetch satisfies the offer — whether the operator tapped Refresh or a mutation's own
  // refetch landed. Keyed on `dataUpdatedAt` so it fires once per actual arrival of data.
  const dataUpdatedAt = worklistQuery.dataUpdatedAt;
  useEffect(() => {
    if (dataUpdatedAt > 0) clearQueryStale('press-worklist');
  }, [dataUpdatedAt]);

  // Retire entries the server has now confirmed. Runs against the RAW cached result, which is why the
  // cache is kept free of optimistic writes. Returns the same map when nothing changed, so React bails
  // out rather than re-rendering.
  useEffect(() => {
    const fresh = worklistQuery.data;
    if (fresh) setPending((p) => prunePending(p, fresh, Date.now()));
  }, [worklistQuery.data]);

  // The TTL sweep. Without it an entry the server never confirms would hold a tick on screen forever, so
  // a garment that still needs pressing would look done and get skipped — the opposite money bug.
  useEffect(() => {
    if (pending.size === 0) return;
    const id = setInterval(() => setPending((p) => expirePending(p, Date.now())), 1_000);
    return () => clearInterval(id);
  }, [pending.size]);

  // Batches with a check-off POST in flight. The mark endpoints are optimistic-locked per batch, so
  // two marks to the SAME batch would race to a 409 — we disable that batch's rows while one is in
  // flight, but leave OTHER batches clickable (the old page-wide `busy` froze the entire grid).
  const [inFlightBatches, setInFlightBatches] = useState<Set<string>>(() => new Set());

  const markMut = useMutation({
    mutationFn: async (vars: MarkVars) => {
      const body = {
        storeId: vars.storeId,
        orderId: vars.orderId,
        lineId: vars.lineId,
        location: vars.location,
        pressed: vars.pressed,
      };
      // Dispatch to the right EXISTING endpoint by technique; both already run settleLine server-side.
      // DTF carries an optional copyIndex for per-copy check-off; screen-print stays coarse (no copy).
      const res =
        vars.method === 'dtf'
          ? await client.POST('/batches/{batchId}/press', {
              params: { path: { batchId: vars.batchId } },
              body: vars.copyIndex !== undefined ? { ...body, copyIndex: vars.copyIndex } : body,
            })
          : await client.POST('/screen-print/worklist/mark', {
              body: { batchId: vars.batchId, ...body },
            });
      if (res.error) throw new Error(res.error?.error?.detail ?? 'Failed to update.');
      if (!res.data?.ok) throw new Error('Empty response from the server.');
      return res.data.data;
    },
    /**
     * Optimistic check-off: record the mark so the derived worklist shows it immediately, instead of
     * waiting for the POST plus the (heavy) refetch. `cancelQueries` still runs — a refetch already in
     * flight is wasted work — but it is no longer load-bearing, because a response that arrives WITHOUT
     * this mark now gets it re-applied rather than un-ticking the operator's box.
     */
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: ['press-worklist'] });
      setPending((p) => addPending(p, vars, new Date().toISOString(), Date.now()));
      setInFlightBatches((s) => new Set(s).add(vars.batchId));
    },
    /**
     * Rollback is now just forgetting the mark — the derived worklist falls back to server truth for that
     * one placement, and every other pending mark is untouched. This replaces the old surgical re-flip,
     * which had to invert `pressed` and write it back into the cache.
     */
    onError: (_e, vars) => {
      setPending((p) => dropPending(p, vars));
    },
    // Reconcile in the background — no longer on the critical path, the box already flipped. A 409
    // means it changed under us, so pull fresh state. A press can complete/uncomplete the garment
    // (line flips PRINTED, a batch drops off once every placement is pressed), so refresh the batch +
    // order too to keep the Gang Sheets page and the order job card consistent.
    onSettled: (_d, _e, v) => {
      setInFlightBatches((s) => {
        const n = new Set(s);
        n.delete(v.batchId);
        return n;
      });
      qc.invalidateQueries({ queryKey: ['press-worklist'] });
      qc.invalidateQueries({ queryKey: ['batches'] });
      qc.invalidateQueries({ queryKey: ['batch', v.batchId] });
      qc.invalidateQueries({ queryKey: ['order', v.storeId, v.orderId] });
      // Pressing the last placement settles the garment, and `settleLine` CONSUMES its blank — so
      // on-hand, the reorder thresholds, and the draft PO all just moved. Without this the inventory
      // hub keeps showing pre-press stock until someone hard-reloads, which is how a blank gets
      // ordered that was already used (or not ordered that was).
      invalidateInventory(qc);
      // The production board derives ship/don't-ship from line workflow state, which a settle flips.
      qc.invalidateQueries({ queryKey: ['production-board'] });
    },
  });

  // Bulk "finish the whole screen-print run" — a screen is set up once and every garment runs under it,
  // so the operator marks them all pressed together (DTF stays per-garment). SEQUENTIAL by necessity:
  // /screen-print/worklist/mark is optimistic-locked on the batch's updatedAt, so two marks to the same
  // batch in parallel would 409 — each call must read the state the previous one wrote. It's the SAME
  // per-placement endpoint the checkboxes use (each still runs settleLine, so a garment completes and
  // consumes its blank only when its LAST placement is pressed), just driven N times in a row. Partial
  // failures are collected (not thrown), so a mid-run 409 leaves the rest intact and is a safe retry.
  const [bulkProgress, setBulkProgress] = useState<{
    runKey: string;
    done: number;
    total: number;
  } | null>(null);
  const bulkMark = useMutation({
    mutationFn: async (vars: { runKey: string; items: PressRunItem[]; pressed: boolean }) => {
      setBulkProgress({ runKey: vars.runKey, done: 0, total: vars.items.length });
      const failed: PressRunItem[] = [];
      for (const it of vars.items) {
        const res = await client.POST('/screen-print/worklist/mark', {
          body: {
            batchId: it.batchId,
            storeId: it.storeId,
            orderId: it.orderId,
            lineId: it.lineId,
            location: it.location,
            pressed: vars.pressed,
          },
        });
        if (res.error || !res.data?.ok) failed.push(it);
        setBulkProgress((p) => (p ? { ...p, done: p.done + 1 } : p));
      }
      return { failed, total: vars.items.length };
    },
    onSettled: () => {
      // A bulk run can complete/uncomplete many garments across several batches + orders — refresh the
      // whole family (broad prefixes) so the worklist, gang sheets, and every touched order card agree.
      qc.invalidateQueries({ queryKey: ['press-worklist'] });
      qc.invalidateQueries({ queryKey: ['batches'] });
      qc.invalidateQueries({ queryKey: ['batch'] });
      qc.invalidateQueries({ queryKey: ['order'] });
      // A bulk run settles many garments, so it consumes many blanks — same reasoning as the single
      // mark above, at N× the drift.
      invalidateInventory(qc);
      qc.invalidateQueries({ queryKey: ['production-board'] });
      setBulkProgress(null);
    },
  });
  // A bulk run is sequential across several batches and has its own progress UI, so it locks the whole
  // grid while working. An individual mark locks only its own batch (see inFlightBatches), leaving the
  // rest of the worklist clickable.
  const bulkBusy = bulkMark.isPending;
  const isBatchBusy = (batchId: string) => bulkBusy || inFlightBatches.has(batchId);

  /**
   * Server truth + marks awaiting confirmation. Everything below renders from this, never from the raw
   * query data — that separation is what lets `prunePending` tell "the server confirmed it" apart from
   * "we optimistically drew it". Returns the same reference when nothing is pending, so the press-run
   * grouping downstream keeps its memoisation.
   */
  const data = useMemo(
    () => (worklistQuery.data ? reapplyPending(worklistQuery.data, pending) : undefined),
    [worklistQuery.data, pending],
  );
  const batches = data?.batches ?? [];
  // One option per batch (oldest first from the server — queue order). `outstanding` is the garments
  // still to press on that batch, so the operator can pick "the two smallest to knock out" at a glance.
  const batchOptions = useMemo<BatchOption[]>(
    () =>
      batches.map((b) => ({
        batchId: b.batchId,
        label: batchLabelOf(b),
        brand: b.brandLabel ?? b.storeIds.join(', '),
        outstanding: b.items.reduce((s, i) => s + (i.pressedAt ? 0 : i.quantity), 0),
        pull: pullChipLabel(pullState(b), b.status, Date.now(), 'short'),
      })),
    [batches],
  );
  /** batchId → "#219" + the same short pull label, for the run card's strip (a run spans sheets). */
  const pullByBatch = useMemo(
    () =>
      new Map(
        batchOptions.map((o) => [
          o.batchId,
          { label: o.label.replace(/^Batch /, ''), pull: o.pull },
        ]),
      ),
    [batchOptions],
  );
  // Selected batches first (empty = all), then the technique filter; feeds both views + the pull sheet.
  const filteredBatches = useMemo(
    () => filterWorklistBatches(batches, { selectedBatchIds, methodFilter }),
    [batches, methodFilter, selectedBatchIds],
  );
  const runs = useMemo(() => groupByPressRun(filteredBatches), [filteredBatches]);
  const nameBySku = useBlankNames();
  const [printing, setPrinting] = useState(false);
  // Labels for the batches actually still present (a pressed-out selection lingers in the set but
  // shouldn't show), used by the count line, the filter trigger, and the pull-sheet header.
  const selectedBatchLabels = batchOptions
    .filter((o) => selectedBatchIds.has(o.batchId))
    .map((o) => o.label);
  const filterLabel = [
    methodFilter === 'all' ? 'All techniques' : methodLabelOf(methodFilter),
    selectedBatchLabels.length > 0 ? selectedBatchLabels.join(', ') : null,
  ]
    .filter(Boolean)
    .join(' · ');
  // Count what's actually shown (server totals cover all techniques, which would mislead under a filter).
  const outstanding = useMemo(
    () =>
      filteredBatches.reduce(
        (sum, b) => sum + b.items.reduce((s, i) => s + (i.pressedAt ? 0 : i.quantity), 0),
        0,
      ),
    [filteredBatches],
  );

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Brush className="size-6" /> Press station
          </h1>
          <p className="text-sm text-muted-foreground">
            Every READY gang sheet with garments still to press — screen print and DTF — oldest
            first. Check off each placement as you press it; a garment is done once all its
            placements (both techniques) are pressed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPrinting(true)}
            disabled={runs.length === 0}
            title="Print the blanks-to-pull sheet for the press runs shown"
          >
            <Printer className="size-4" />
            Print pull sheet
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => worklistQuery.refetch()}
            disabled={worklistQuery.isFetching}
            // The badge is the whole message, so it has to be in the accessible name too — a shop iPad
            // in Guided Access is exactly where a colour-only cue gets missed.
            aria-label={worklistMoved ? 'Refresh — the worklist has changed' : 'Refresh'}
            title={
              worklistMoved
                ? 'Another station changed the worklist — tap to load it'
                : 'Reload the worklist'
            }
            // No layout change when the badge appears: the dot is absolutely positioned inside a
            // zero-width wrapper, so nothing on this toolbar shifts under a finger.
            className={cn(worklistMoved && 'border-primary text-primary')}
          >
            <span className="relative inline-flex">
              <RefreshCw className={cn('size-4', worklistQuery.isFetching && 'animate-spin')} />
              {worklistMoved && (
                <span
                  aria-hidden="true"
                  className="absolute -right-1.5 -top-1.5 size-2 rounded-full bg-primary"
                />
              )}
            </span>
            Refresh
          </Button>
        </div>
      </header>

      {data && batches.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{outstanding}</span> garment
            {outstanding === 1 ? '' : 's'} to press across{' '}
            {view === 'run' ? (
              <>
                <span className="font-medium text-foreground">{runs.length}</span> press run
                {runs.length === 1 ? '' : 's'}.
              </>
            ) : (
              <>
                <span className="font-medium text-foreground">{filteredBatches.length}</span> batch
                {filteredBatches.length === 1 ? '' : 'es'}.
              </>
            )}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            {batchOptions.length > 1 && (
              <BatchFilter
                options={batchOptions}
                selected={selectedBatchIds}
                onToggle={toggleBatch}
                onClear={() => setSelectedBatchIds(new Set())}
              />
            )}
            <ToggleGroup
              type="single"
              value={methodFilter}
              onValueChange={(v) => {
                if (v === 'all' || v === 'screen-print' || v === 'dtf') setMethodFilter(v);
              }}
              variant="outline"
              size="sm"
            >
              <ToggleGroupItem value="all">All</ToggleGroupItem>
              <ToggleGroupItem value="screen-print">Screen print</ToggleGroupItem>
              <ToggleGroupItem value="dtf">DTF</ToggleGroupItem>
            </ToggleGroup>
            <ToggleGroup
              type="single"
              value={view}
              onValueChange={(v) => {
                if (v === 'batch' || v === 'run') setView(v);
              }}
              variant="outline"
              size="sm"
            >
              <ToggleGroupItem value="batch">By batch</ToggleGroupItem>
              <ToggleGroupItem value="run">By press run</ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>
      )}

      {markMut.isError && markMut.error instanceof Error && (
        <Alert variant="destructive">
          <AlertDescription>{markMut.error.message}</AlertDescription>
        </Alert>
      )}

      {bulkMark.data && bulkMark.data.failed.length > 0 && (
        <Alert variant="destructive">
          <AlertTitle>
            {bulkMark.data.failed.length} garment{bulkMark.data.failed.length === 1 ? '' : 's'}{' '}
            couldn’t be marked
          </AlertTitle>
          <AlertDescription>
            The batch changed mid-run (or a network hiccup). The list has been refreshed with what
            did go through — click the run again to finish the rest.
          </AlertDescription>
        </Alert>
      )}

      {worklistQuery.isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn’t load the worklist</AlertTitle>
          <AlertDescription>
            {worklistQuery.error instanceof Error
              ? worklistQuery.error.message
              : 'Unknown error. Try refreshing.'}
          </AlertDescription>
        </Alert>
      )}

      {worklistQuery.isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {data && batches.length === 0 && !worklistQuery.isError && (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <CheckCircle2 className="size-8 text-emerald-600 dark:text-emerald-400" />
            <p className="font-medium">All caught up</p>
            <p className="text-sm text-muted-foreground">
              No READY batch has garments waiting to press. New work shows up here once a batch is
              composited.
            </p>
          </CardContent>
        </Card>
      )}

      {data && batches.length > 0 && filteredBatches.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center text-sm text-muted-foreground">
            <span>Nothing to press for the batches and technique you picked.</span>
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          </CardContent>
        </Card>
      )}

      {view === 'batch' &&
        filteredBatches.map((batch) => (
          <BatchCard
            key={batch.batchId}
            batch={batch}
            disabled={isBatchBusy(batch.batchId)}
            onToggle={(item, pressed) =>
              markMut.mutate({
                method: item.method,
                batchId: batch.batchId,
                storeId: item.storeId,
                orderId: item.orderId,
                lineId: item.lineId,
                location: item.location,
                pressed,
              })
            }
            onToggleCopy={(item, copyIndex, pressed) =>
              markMut.mutate({
                method: item.method,
                batchId: batch.batchId,
                storeId: item.storeId,
                orderId: item.orderId,
                lineId: item.lineId,
                location: item.location,
                pressed,
                copyIndex,
              })
            }
          />
        ))}

      {view === 'run' &&
        runs.map((run) => (
          <PressRunCard
            key={run.key}
            run={run}
            nameBySku={nameBySku}
            pullByBatch={pullByBatch}
            isBusy={isBatchBusy}
            progress={bulkProgress?.runKey === run.key ? bulkProgress : null}
            onToggle={(item, pressed) =>
              markMut.mutate({
                method: item.method,
                batchId: item.batchId,
                storeId: item.storeId,
                orderId: item.orderId,
                lineId: item.lineId,
                location: item.location,
                pressed,
              })
            }
            onToggleCopy={(item, copyIndex, pressed) =>
              markMut.mutate({
                method: item.method,
                batchId: item.batchId,
                storeId: item.storeId,
                orderId: item.orderId,
                lineId: item.lineId,
                location: item.location,
                pressed,
                copyIndex,
              })
            }
            onBulkPress={(items, pressed) => bulkMark.mutate({ runKey: run.key, items, pressed })}
          />
        ))}

      {printing && (
        <PressPullSheet
          runs={runs}
          nameBySku={nameBySku}
          filterLabel={filterLabel}
          onClose={() => setPrinting(false)}
        />
      )}
    </div>
  );
}

/**
 * The check-off control for one placement row. Screen-print and single-copy DTF keep the original
 * single checkbox (whole-placement toggle). A DTF placement with >1 copy renders one small checkbox
 * per physical garment (copy 1..N) so the operator checks each shirt off as it comes off the press —
 * the qty-N staff pain. A copy is checked when it is in {@link pressedCopiesOf} (a fully-pressed item
 * with a scalar `pressedAt` shows every copy checked). The server rejects pressing a refunded copy.
 */
function PressCheckoff({
  item,
  disabled,
  onToggle,
  onToggleCopy,
}: {
  item: PressWorkItem;
  disabled: boolean;
  onToggle: (pressed: boolean) => void;
  onToggleCopy: (copyIndex: number, pressed: boolean) => void;
}) {
  if (!isPerCopy(item)) {
    const pressed = Boolean(item.pressedAt);
    return (
      <Checkbox
        checked={pressed}
        disabled={disabled}
        onCheckedChange={() => onToggle(!pressed)}
        aria-label={`Mark ${methodLabelOf(item.method)} ${item.location} of ${item.orderName} ${pressed ? 'not pressed' : 'pressed'}`}
      />
    );
  }
  const pressedSet = new Set(pressedCopiesOf(item));
  return (
    <div className="flex flex-wrap gap-x-2 gap-y-1">
      {Array.from({ length: item.quantity }, (_, k) => {
        const cp = pressedSet.has(k);
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: copyIndex is a physical copy's stable identity — copy k is always copy k, copies never reorder.
          <div key={k} className="flex items-center gap-1">
            <Checkbox
              checked={cp}
              disabled={disabled}
              onCheckedChange={() => onToggleCopy(k, !cp)}
              aria-label={`Mark copy ${k + 1} of ${item.quantity} — ${methodLabelOf(item.method)} ${item.location} of ${item.orderName} ${cp ? 'not pressed' : 'pressed'}`}
            />
            <span className="text-[10px] tabular-nums text-muted-foreground">{k + 1}</span>
          </div>
        );
      })}
    </div>
  );
}

function BatchCard({
  batch,
  disabled,
  onToggle,
  onToggleCopy,
}: {
  batch: PressWorklistBatch;
  disabled: boolean;
  onToggle: (item: PressWorkItem, pressed: boolean) => void;
  onToggleCopy: (item: PressWorkItem, copyIndex: number, pressed: boolean) => void;
}) {
  const label = batchLabelOf(batch);
  const brand = batch.brandLabel ?? batch.storeIds.join(', ');
  const pressedCount = batch.items.filter((i) => i.pressedAt).length;

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          {label}
          <span className="text-sm font-normal text-muted-foreground">· {brand}</span>
        </CardTitle>
        <div className="flex items-center gap-2">
          <PullChip state={pullState(batch)} status={batch.status} now={Date.now()} form="short" />
          <Badge variant="outline" className="text-muted-foreground">
            {pressedCount}/{batch.items.length} pressed
          </Badge>
          <span className="text-xs text-muted-foreground">
            {relativeAge(batch.createdAt, Date.now())}
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full min-w-[620px] text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="w-10 px-3 py-2" />
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Order</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Garment</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Technique</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Location</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Design</th>
                <th className="px-3 py-2 text-right font-medium text-muted-foreground">Qty</th>
              </tr>
            </thead>
            <tbody>
              {batch.items.map((item) => {
                const pressed = Boolean(item.pressedAt);
                const key = `${item.method}#${item.storeId}#${item.orderId}#${item.lineId}#${item.location}`;
                return (
                  <tr
                    key={key}
                    className={cn(
                      'border-t [&:nth-child(even)]:bg-muted/20',
                      pressed && 'text-muted-foreground',
                    )}
                  >
                    <td className="px-3 py-1.5 align-top">
                      <PressCheckoff
                        item={item}
                        disabled={disabled}
                        onToggle={(p) => onToggle(item, p)}
                        onToggleCopy={(copyIndex, p) => onToggleCopy(item, copyIndex, p)}
                      />
                    </td>
                    <td className="px-3 py-1.5 align-top font-medium">{item.orderName}</td>
                    <td className="px-3 py-1.5 align-top text-xs">
                      {item.title}
                      {item.variantTitle && (
                        <span className="text-muted-foreground"> · {item.variantTitle}</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 align-top">
                      <MethodBadge method={item.method} />
                      {item.fromStock && (
                        <span className="mt-0.5 block text-[10px] font-medium text-amber-700 dark:text-amber-400">
                          from shelf stock
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 align-top text-xs">{item.location}</td>
                    <td className="px-3 py-1.5 align-top text-xs">
                      {item.designName ?? <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-3 py-1.5 text-right align-top font-medium tabular-nums">
                      {item.quantity}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function PressRunCard({
  run,
  nameBySku,
  pullByBatch,
  isBusy,
  progress,
  onToggle,
  onToggleCopy,
  onBulkPress,
}: {
  run: PressRun;
  nameBySku: ReadonlyMap<string, BlankOnHand>;
  /** Each sheet's pull state in short form, keyed by batchId — shown on the Blanks to pull strip. */
  pullByBatch: ReadonlyMap<string, { label: string; pull: string }>;
  /** Whether a given batch has a check-off in flight (or a bulk run is going) — a run spans batches,
   *  so each row is gated by its own item's batch, not the whole card. */
  isBusy: (batchId: string) => boolean;
  /** Set while THIS run is being bulk-marked, for the inline "Marking n/m" progress. */
  progress: { done: number; total: number } | null;
  onToggle: (item: PressRunItem, pressed: boolean) => void;
  /** DTF per-copy check-off: mark one copy of a placement pressed/unpressed. */
  onToggleCopy: (item: PressRunItem, copyIndex: number, pressed: boolean) => void;
  /** Mark (or unmark) a set of this run's placements pressed in one go — screen print only. */
  onBulkPress: (items: PressRunItem[], pressed: boolean) => void;
}) {
  // A screen print run finishes as ONE setup — mark every garment pressed together (DTF is per-garment,
  // pressed one transfer at a time, so it gets no bulk action). Toggle: mark the unpressed ones, or —
  // once all are pressed — unmark them all (the reversal ships with the action).
  const isScreenPrint = run.method === 'screen-print';
  const unpressed = run.items.filter((i) => !i.pressedAt);
  const allPressed = run.items.length > 0 && unpressed.length === 0;
  const bulkTargets = allPressed ? run.items.filter((i) => i.pressedAt) : unpressed;
  // The bulk button touches every batch in the run, so gate it if ANY of them has a mark in flight.
  const anyBusy = run.items.some((i) => isBusy(i.batchId));
  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex flex-wrap items-center gap-2 text-base">
          <MethodBadge method={run.method} />
          <span className="capitalize">{run.location}</span>
          <span className="text-sm font-normal text-muted-foreground">· {run.designLabel}</span>
        </CardTitle>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-muted-foreground">
            {run.pressedGarments}/{run.totalGarments} garments pressed
          </Badge>
          {isScreenPrint && (
            <Button
              variant="outline"
              size="sm"
              disabled={anyBusy || bulkTargets.length === 0}
              onClick={() => onBulkPress(bulkTargets, !allPressed)}
            >
              {progress ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  Marking {progress.done}/{progress.total}
                </>
              ) : allPressed ? (
                'Unmark all'
              ) : (
                `Mark all pressed (${unpressed.length})`
              )}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <BlankPullStrip
          rows={blankPullList(run.items, nameBySku)}
          sheets={[...new Set(run.items.map((i) => i.batchId))]
            .map((id) => pullByBatch.get(id))
            .filter((x): x is { label: string; pull: string } => x != null)}
        />
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full min-w-[520px] text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="w-10 px-3 py-2" />
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Order</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Garment</th>
                <th className="px-3 py-2 text-left font-medium text-muted-foreground">Batch</th>
                <th className="px-3 py-2 text-right font-medium text-muted-foreground">Qty</th>
              </tr>
            </thead>
            <tbody>
              {run.items.map((item) => {
                const pressed = Boolean(item.pressedAt);
                const key = `${item.batchId}#${item.storeId}#${item.orderId}#${item.lineId}#${item.location}`;
                return (
                  <tr
                    key={key}
                    className={cn(
                      'border-t [&:nth-child(even)]:bg-muted/20',
                      pressed && 'text-muted-foreground',
                    )}
                  >
                    <td className="px-3 py-1.5 align-top">
                      <PressCheckoff
                        item={item}
                        disabled={isBusy(item.batchId)}
                        onToggle={(p) => onToggle(item, p)}
                        onToggleCopy={(copyIndex, p) => onToggleCopy(item, copyIndex, p)}
                      />
                    </td>
                    <td className="px-3 py-1.5 align-top font-medium">{item.orderName}</td>
                    <td className="px-3 py-1.5 align-top text-xs">
                      {item.title}
                      {item.variantTitle && (
                        <span className="text-muted-foreground"> · {item.variantTitle}</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 align-top text-xs text-muted-foreground">
                      {item.batchSeq ? `#${item.batchSeq}` : item.batchId.slice(0, 8)}
                    </td>
                    <td className="px-3 py-1.5 text-right align-top font-medium tabular-nums">
                      {item.quantity}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
